import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { IUser, IDropdown, IUserDropdown } from "../../models/BaseModel";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { Fetch } from "../../shared/Fetch";
import { JVInput } from "../../shared/JVInput";
import { JVCheckbox } from "../../shared/JVCheckbox";
import { JVMultiselect } from "../../shared/JVMultiselect";
import { JVDropdown } from "../../shared/JVDropdown";

interface Iprops {
    singleData: IUser,
    open: boolean,
    hideDialog: () => void,
    toast: any,
    reloadData: () => void,
    hasPermission: (pageName: string, featureName?: string) => boolean
}

export const Form = (props: Iprops) => {
    const [roles, setRoles] = useState<IDropdown[]>();
    const [departments, setDepartments] = useState<IDropdown[]>();
    const [selectedRoles, setSelectedRoles] = useState<IDropdown[]>();
    const [submitting, setSubmitting] = useState(false);
    const [manager, setManager] = useState<IUserDropdown[]>();
    const [teamLead, setTeamLead] = useState<IUserDropdown[]>();


    const schema = yup.object({
        name: yup.string()
            .required("Please enter Name.")
            .min(3, "Name must be at least 3 characters long."),


        email: yup.string()
            .required("Please provide email address.")
            .email("Please enter a valid email address."),

        password: !props.singleData.id ? yup.string()
            .required("A password is required to proceed.")
            .min(6, "Password must be at least 6 characters long.") :
            yup.string().nullable(),

        isActive: yup
            .boolean()
            .default(true)
            .required('Please specify if the user is active.'),
    });

    const { control, handleSubmit, formState: { errors }, reset } = useForm<IUser>({
        resolver: yupResolver(schema),
        defaultValues: props.singleData,
        shouldFocusError: false,
    });

    useEffect(() => {
        loadRoles();
        loadDepartments();
        loadTeamLead();
        loadManager();
    }, []);

    useEffect(() => {
        reset(props.singleData);
        if (props.singleData.id) {
            loadAssignedRoles(props.singleData.id);
        } else {
            setSelectedRoles([]);
        }
    }, [props.singleData]);


    const loadRoles = async () => {
        const response = await Fetch("role/dropdown", "GET", null, null, null);
        if (response) {
            setRoles(await response.json());
        }
    }

    const loadAssignedRoles = async (id: number) => {
        const response = await Fetch(`user/roles/${id}`, "GET", null, null, null);
        if (response) {
            setSelectedRoles(await response.json());
        }
    }

    const loadDepartments = async () => {
        const response = await Fetch("department/dropdown", "GET", null, null, null);
        if (response) {
            setDepartments(await response.json());
        }
    }

    const loadManager = async () => {
        const response = await Fetch("dropdown/user", "GET", null, null, null);
        if (response) {
            // setManager(await response.json());
            const data = await response.json();
            setManager([
                { id: null, name: '-- Select Manager --' },
                ...data
            ]);
        }
    }

    const loadTeamLead = async () => {
        const response = await Fetch("dropdown/user", "GET", null, null, null);
        if (response) {
            const data = await response.json();

            setTeamLead([
                { id: null, name: '-- Select Team Lead --' },
                ...data
            ]);
        }
    }

    const saveToDB = async (body: IUser) => {
        setSubmitting(true);
        const method = props.singleData.id ? "PUT" : "POST";
        const message = props.singleData.id ? "Updated Successfully" : "Added Successfully";

        const roles: Array<number> = selectedRoles?.map(role => role.id) || [];

        const response = await Fetch(`user${props.singleData.id ? '/' + props.singleData.id : ''}`, method, { ...body, roles }, null, props.toast);
        if (response) {
            console.log(1)
            onHide();
            props.toast.current.show({ severity: 'success', detail: message, life: 3000 });
            props.reloadData();
        }
        setSubmitting(false);
    }

    const onHide = () => {
        props.hideDialog();
        reset({});
        setSelectedRoles(undefined);
    }

    function onSubmit(data: any) {
        if (selectedRoles === undefined || selectedRoles.length < 1) {
            props.toast.current.show({ severity: 'error', detail: "Select at least one Role", life: 3000 });
            return;
        }
        saveToDB(data);
    }

    return (
        <Dialog visible={props.open} style={{ width: '60vw' }} header="Add/Edit User" modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(onSubmit)} className="formgrid grid p-3">

                {/* Personal Details Section */}
                <div className="col-12 text-lg font-bold mb-3 border-bottom-1 surface-border py-2">Personal Details</div>
                <JVInput required label="Name" control={control} name="name" error={errors.name?.message} />
                <JVInput required label="Email" control={control} name="email" error={errors.email?.message} />
                <JVInput required={!props.singleData.id} label="Password" control={control} name="password" error={errors.password?.message} />
                <JVCheckbox label="Active Status" control={control} name="isActive" />

                {/* Employment Details Section */}
                <div className="col-12 text-lg font-bold mb-3 mt-3 border-bottom-1 surface-border py-2">Employment Details</div>
                <JVInput disabled={props.singleData.employeeCode != null} label="Employee Code" control={control} name="employeeCode" />
                <JVDropdown required label="Department" control={control} options={departments} optionLabel="department_Name" optionValue="id" name="department_ID" placeholder="Select Department" error={errors.departmentId?.message} />
                <JVDropdown label="Manager" control={control} options={manager} optionLabel="name" optionValue="id" name="manager_Id" placeholder="Select Manager" />
                <JVDropdown label="Team Lead" control={control} options={teamLead} optionLabel="name" optionValue="id" name="teamLead_Id" placeholder="Select Team Lead" />

                {/* Role Configuration Section */}
                <div className="col-12 text-lg font-bold mb-3 mt-3 border-bottom-1 surface-border py-2">Role </div>
                <JVMultiselect column={1} label="Assign Roles" options={roles} optionLabel="name" placeholder="Select Roles" required value={selectedRoles} onChange={setSelectedRoles} />

                <div className="col-12 flex flex-wrap gap-4 mt-2">
                    <JVCheckbox label="Is Manager" control={control} name="isManger" column={3} />
                    <JVCheckbox label="Is Team Lead" control={control} name="isTeamLead" column={3} />
                </div>

                <div className="col-12 flex justify-content-end gap-2 mt-4 pt-3 border-top-1 surface-border">
                    <Button label="CLOSE" onClick={onHide} type="button" className="p-button-outlined p-button-secondary w-8rem" />
                    <Button loading={submitting} label="SAVE" type="submit" className="p-button-primary w-8rem" />
                </div>
            </form>
        </Dialog>
    );
}
